// A deliberately small YAML reader for pasted samples (block mappings, block
// sequences, scalars, quoted strings, flow [..]/{..} that happen to be JSON,
// comments). It refuses what it can't read faithfully - block scalars (| >),
// anchors/aliases, tags, multi-document streams - with an error rather than
// guessing, because a wrong parse would generate a wrong mapping.
import type { JsonValue } from './mappingEngine';

interface Line {
  indent: number;
  text: string;
}

function stripComment(s: string): string {
  let q = '';
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === q) q = '';
    } else if (c === '"' || c === "'") q = c;
    else if (c === '#' && (i === 0 || /\s/.test(s[i - 1]))) return s.slice(0, i);
  }
  return s;
}

function scalar(raw: string): JsonValue {
  const v = raw.trim();
  if (v === '' || v === '~' || v === 'null') return null;
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(v)) return Number(v);
  if ((v.startsWith('"') && v.endsWith('"') && v.length >= 2) || (v.startsWith("'") && v.endsWith("'") && v.length >= 2)) {
    return v[0] === '"' ? (JSON.parse(v) as string) : v.slice(1, -1).replace(/''/g, "'");
  }
  if (v.startsWith('[') || v.startsWith('{')) {
    try {
      return JSON.parse(v) as JsonValue;
    } catch {
      throw new Error('YAML flow collections are only supported when they are valid JSON.');
    }
  }
  if (/^[|>&*!]/.test(v)) throw new Error('YAML block scalars, anchors and tags are not supported.');
  return v;
}

const KEY = /^((?:"[^"]*"|'[^']*'|[^\s"'#][^:]*?)):(?:\s+(.*)|)$/;

export function parseSimpleYaml(text: string): JsonValue {
  const lines: Line[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    if (rawLine.trim() === '---') {
      if (lines.length) throw new Error('Multi-document YAML is not supported.');
      continue;
    }
    if (rawLine.includes('\t') && /^\s*\t/.test(rawLine)) throw new Error('YAML must be indented with spaces.');
    const stripped = stripComment(rawLine).trimEnd();
    if (stripped.trim() === '') continue;
    lines.push({ indent: stripped.length - stripped.trimStart().length, text: stripped.trim() });
  }
  if (!lines.length) throw new Error('Empty YAML.');
  let i = 0;

  const keyName = (k: string): string => (k[0] === '"' || k[0] === "'" ? (scalar(k) as string) : k.trim());

  const parseNode = (): JsonValue => {
    const l = lines[i];
    if (l.text === '-' || l.text.startsWith('- ')) return parseSeq(l.indent);
    if (KEY.test(l.text)) return parseMap(l.indent);
    i++;
    return scalar(l.text);
  };

  const child = (parentIndent: number, allowSameIndentSeq: boolean): JsonValue => {
    const n = lines[i];
    if (!n) return null;
    if (n.indent > parentIndent) return parseNode();
    if (allowSameIndentSeq && n.indent === parentIndent && (n.text === '-' || n.text.startsWith('- '))) return parseSeq(n.indent);
    return null;
  };

  const parseMap = (indent: number): JsonValue => {
    const obj: Record<string, JsonValue> = {};
    while (i < lines.length && lines[i].indent === indent) {
      const m = KEY.exec(lines[i].text);
      if (!m) break;
      i++;
      const k = keyName(m[1]);
      obj[k] = m[2] === undefined || m[2].trim() === '' ? child(indent, true) : scalar(m[2]);
    }
    return obj;
  };

  const parseSeq = (indent: number): JsonValue => {
    const arr: JsonValue[] = [];
    while (i < lines.length && lines[i].indent === indent && (lines[i].text === '-' || lines[i].text.startsWith('- '))) {
      const rest = lines[i].text.slice(1).trim();
      if (rest === '') {
        i++;
        arr.push(child(indent, false));
      } else if (KEY.test(rest)) {
        // "- key: v" opens a mapping whose other keys sit at the column of `key`.
        const col = indent + (lines[i].text.length - lines[i].text.slice(1).trimStart().length);
        lines[i] = { indent: col, text: rest };
        arr.push(parseMap(col));
      } else {
        i++;
        arr.push(scalar(rest));
      }
    }
    return arr;
  };

  const root = parseNode();
  if (i < lines.length) throw new Error(`Could not read YAML near "${lines[i].text}".`);
  return root;
}
